import { useCallback, useEffect, useRef, type ReactNode } from "react"

import { Card, EditableTitle, type EditableTitleProps } from "@coval/ui"

export type ResourceNameCardProps = Pick<
  EditableTitleProps,
  | "value"
  | "onSave"
  | "label"
  | "placeholder"
  | "maxLength"
  | "validate"
  | "error"
  | "disabled"
  | "saving"
  | "focusOnMount"
> & {
  /**
   * Called with true once focus leaves the field and it stays open, which
   * means its name was rejected, and with false when the field closes, takes
   * focus again, or the card unmounts.
   */
  onRejectedNameChange?: (rejected: boolean) => void
  /** Sits at the right end of the name row, such as a tag or avatar control. */
  trailing?: ReactNode
  /** Rendered under the name row, inside the card. */
  children?: ReactNode
  /** Marks the card invalid for an error in its trailing control, described by `errorId`. */
  invalid?: boolean
  /** The id of the element under the name row that describes `invalid`. */
  errorId?: string
  "data-testid"?: string
}

/**
 * The name card at the top of a create or edit page, styled like the run name
 * card in the simulation launcher. The field opens focused unless `focusOnMount`
 * is false, so a new resource is named first; Enter or blur puts the name into
 * the page draft.
 */
export function ResourceNameCard({
  "data-testid": testId,
  focusOnMount = true,
  trailing,
  children,
  invalid = false,
  errorId,
  onRejectedNameChange,
  ...titleProps
}: ResourceNameCardProps) {
  const onRejectedNameChangeRef = useRef(onRejectedNameChange)
  onRejectedNameChangeRef.current = onRejectedNameChange
  const isOpenRef = useRef(focusOnMount && !titleProps.disabled)
  const handleEditingChange = useCallback((open: boolean) => {
    isOpenRef.current = open
    if (!open) {
      onRejectedNameChangeRef.current?.(false)
    }
  }, [])
  // A card that unmounts with a rejected name, such as on a remount by key,
  // must not leave a guard blocking a field that is gone.
  useEffect(() => {
    onRejectedNameChangeRef.current?.(false)
    return () => onRejectedNameChangeRef.current?.(false)
  }, [])

  return (
    <Card
      aria-invalid={invalid ? true : undefined}
      aria-describedby={invalid ? errorId : undefined}
      className={invalid ? "border-coval-danger-border" : undefined}
    >
      <div className="p-4" data-testid={testId}>
        <div className="flex items-center justify-between gap-4">
          {/* EditableTitle commits on blur before this handler runs: a valid
              name closes the field, so a field still open has a rejected name. */}
          <div
            className="min-w-0 flex-1"
            onFocus={() => onRejectedNameChangeRef.current?.(false)}
            onBlur={(event) => {
              if (
                isOpenRef.current &&
                !event.currentTarget.contains(event.relatedTarget)
              ) {
                onRejectedNameChangeRef.current?.(true)
              }
            }}
          >
            <EditableTitle
              {...titleProps}
              focusOnMount={focusOnMount}
              onEditingChange={handleEditingChange}
            />
          </div>
          {trailing ? <div className="shrink-0">{trailing}</div> : null}
        </div>
        {children}
      </div>
    </Card>
  )
}

/**
 * Tracks whether a name card holds a rejected name, for a page whose Save runs
 * outside the card. Clicking Save blurs the field first, which commits a valid
 * name, so `hasRejectedName()` is true only for a name the field refused; the
 * save stops instead of keeping the previous name. Pass
 * `onRejectedNameChange` to the card.
 */
export function useResourceNameEditGuard() {
  const isRejectedRef = useRef(false)
  const onRejectedNameChange = useCallback((rejected: boolean) => {
    isRejectedRef.current = rejected
  }, [])
  const hasRejectedName = useCallback(() => isRejectedRef.current, [])
  return { onRejectedNameChange, hasRejectedName }
}
